import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {
    classNames,
    Dialog,
    DialogAlignment,
    layout,
    PushButton,
    TabBar,
    TabItem,
    theme,
    useCountdown,
    useIdle,
    usePreventAutoDeactivate,
    View,
    ViewContent
} from "@variocube/cube-ui";
import {Item, Rental} from "../types";
import {stylesheet} from "typestyle";
import {ItemTab} from "./ItemTab";
import {BoxTab} from "./BoxTab";
import {Box} from "@variocube/app-api";
import {useLocalization} from "../../i18n";

export interface AdminViewProps {
    rentals: Rental[];
    items: Item[];
    itemsForInspection?: Item[];
    onDone: () => void;
    onOpenItem: (item: Item) => void;
    onInspectItem: (item: Item) => void;
    onOpenBox: (box: Box) => void;
}

enum AdminTab {
    Items,
    Inspection,
    Boxes
}

export function AdminHomeView({rentals, items, itemsForInspection, onDone, onOpenItem, onInspectItem, onOpenBox}: AdminViewProps) {
    const {t} = useLocalization();
    const [active, setActive] = useState<AdminTab>(AdminTab.Items);

    const handleItemsClick = useCallback(() => setActive(AdminTab.Items), []);
    const handleInspectionClick = useCallback(() => setActive(AdminTab.Inspection), []);
    const handleBoxesClick = useCallback(() => setActive(AdminTab.Boxes), []);

    const idle = useIdle(30);
    const left = useCountdown(10, idle);

    usePreventAutoDeactivate();

    useEffect(() => {
        if (left <= 0) {
            onDone();
        }
    }, [left, onDone]);

    return (
        <View className={layout.vertical}>
            <div className={classNames(layout.fixed, layout.horizontal)}>
                <TabBar className={layout.fill}>
                    <TabItem active={active == AdminTab.Items} onClick={handleItemsClick}>
                        {t("Items")}
                    </TabItem>
                    {(itemsForInspection && itemsForInspection.length > 0) &&
                    <TabItem active={active == AdminTab.Inspection} onClick={handleInspectionClick}>
                        {t("Inspection")}
                    </TabItem>
                    }
                    <TabItem active={active == AdminTab.Boxes} onClick={handleBoxesClick}>
                        {t("Boxes")}
                    </TabItem>
                </TabBar>
                <div className={styles.doneButton} onClick={onDone}>
                    <div>{t("Logout")}</div>
                </div>
            </div>
            <ViewContent>
                {active == AdminTab.Items && (
                    <ItemTab items={items} rentals={rentals} onOpen={onOpenItem}/>
                )}
                {(itemsForInspection && active == AdminTab.Inspection) && (
                    <ItemTab items={itemsForInspection} rentals={[]} onOpen={onInspectItem} />
                )}
                {active == AdminTab.Boxes && (
                    <BoxTab items={items} rentals={rentals} onOpen={onOpenBox}/>
                )}
            </ViewContent>
            <Dialog open={idle} align={DialogAlignment.Center}>
                <div className={classNames(layout.vertical, layout.alignCenter)}>
                    <div className={classNames(theme.moreContrast, layout.paddingMedium)}>
                        {t("AutoLogoutMessage", {remainingSeconds: left})}
                    </div>
                    <div className={classNames(layout.paddingMedium)}>
                        <PushButton>
                            {t("Close")}
                        </PushButton>
                    </div>
                </div>
            </Dialog>
        </View>
    )
}


const styles = stylesheet({
    tabContent: {
        padding: "0.75em",
    },
    doneButton: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0.25rem 0.75rem",
        background: "var(--color-primary)",
        color: "#fff",
        textTransform: "uppercase",
        fontWeight: 500,
        fontSize: "1.25em",
    }
});
